import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Building2, Loader2, CheckCircle, MapPin, Sparkles, Info, Globe, Instagram, Linkedin, Facebook, Twitter } from 'lucide-react';
import AddressAutocomplete from '../../components/AddressAutocomplete';

const INDUSTRY_OPTIONS = [
  'Retail', 'Hospitality', 'Food & Beverage', 'Health & Wellness', 'Beauty & Cosmetics',
  'Technology', 'Automotive', 'Sports & Fitness', 'Entertainment', 'Real Estate',
  'Education', 'Finance', 'Marketing & Advertising', 'Nonprofit', 'Manufacturing',
  'Consumer Goods', 'Fashion', 'Home Services', 'Other',
];

const EVENT_TYPE_OPTIONS = [
  'Brand Activations', 'Product Launches', 'Trade Shows', 'Sampling Events',
  'In-Store Demos', 'Festivals & Fairs', 'Conferences', 'Pop-up Shops',
  'Sporting Events', 'Concerts', 'Grand Openings', 'Holiday/Seasonal',
  'Weddings', 'Corporate Events', 'Private Parties & Celebrations',
  'Galas & Fundraisers', 'Networking Events',
];

const BRAND_PERSONALITY_OPTIONS = [
  'Modern & Bold', 'Friendly & Approachable', 'Luxury & Premium',
  'Playful & Energetic', 'Professional & Trustworthy', 'Eco-Conscious',
  'Cultural & Authentic',
];

const ClientProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    billing_address: '',
  });
  // Iter-245 — About / Brand profile state. Kept separate from formData
  // so each save button only persists its own slice.
  const [aboutData, setAboutData] = useState({
    industry: '',
    company_website: '',
    brand_description: '',
    brand_personality: '',
    products_services: '',
    preferred_event_types: [],
    social_links: { instagram: '', linkedin: '', facebook: '', twitter: '', tiktok: '' },
    ai_company_summary: '',
    about_public: false,
  });
  const [savingAbout, setSavingAbout] = useState(false);
  const [generatingAbout, setGeneratingAbout] = useState(false);
  // Iter-245 — First-login AI auto-suggest. When the About section is
  // blank, automatically pre-generate a draft and prompt the client
  // with Use / Edit / Dismiss CTAs. Dismissal is tracked per-client in
  // localStorage so the prompt never re-fires for the same account.
  const [autoSuggestState, setAutoSuggestState] = useState({
    visible: false,
    loading: false,
    brand_description: '',
    ai_company_summary: '',
    errorMessage: '',
  });

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // Iter-184 — auto-scroll to a hash-anchored section once data loads.
  // Used by the Quick Actions "Edit Profile / Company Info / About Client / Branding"
  // shortcuts that admins use during impersonation.
  useEffect(() => {
    if (loading) return;
    const hash = (location.hash || '').replace('#', '');
    if (!hash) return;
    const el = document.getElementById(`section-${hash}`);
    if (el) {
      // Slight delay so the section transitions/animations settle first.
      const t = setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('ring-2', 'ring-cyan-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-cyan-400'), 1800);
      }, 150);
      return () => clearTimeout(t);
    }
  }, [location.hash, loading]);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/client/profile`, { headers });
      const data = await res.json();
      setProfile(data);
      setFormData({
        company_name: data.company_name || '',
        contact_name: data.contact_name || '',
        billing_address: data.billing_address || '',
      });
      // Iter-245 — hydrate About panel from same payload.
      setAboutData({
        industry: data.industry || '',
        company_website: data.company_website || '',
        brand_description: data.brand_description || '',
        brand_personality: data.brand_personality || '',
        products_services: data.products_services || '',
        preferred_event_types: Array.isArray(data.preferred_event_types) ? data.preferred_event_types : [],
        social_links: {
          instagram: data.social_links?.instagram || '',
          linkedin: data.social_links?.linkedin || '',
          facebook: data.social_links?.facebook || '',
          twitter: data.social_links?.twitter || '',
          tiktok: data.social_links?.tiktok || '',
        },
        ai_company_summary: data.ai_company_summary || '',
        about_public: !!data.about_public,
      });
      // Iter-245 — Trigger AI auto-suggest if About is completely empty
      // AND the user has not previously dismissed it for this client.
      // Iter-257 — Auto-fire requires the SAME minimum payload the
      // backend enforces: company_name + (website OR industry OR
      // products_services). Without it we were 400-ing with a
      // "Generation failed" toast right after the page loaded.
      const dismissKey = `about_autosuggest_dismissed:${data.user_id}`;
      const alreadyDismissed = localStorage.getItem(dismissKey) === '1';
      const isAboutBlank = !data.brand_description && !data.ai_company_summary
        && !data.industry && !data.products_services;
      const haveMinimumSeed = !!data.company_name && (
        !!data.company_website || !!data.industry || !!data.products_services
      );
      if (isAboutBlank && haveMinimumSeed && !alreadyDismissed) {
        // Fire after a small delay so the page paints first.
        setTimeout(() => fetchAutoSuggest(data), 600);
      }
    } catch (e) {
      console.error('Error fetching profile:', e);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoSuggest = async (profileData) => {
    // Show loader immediately on next tick so React batches commit.
    await Promise.resolve();
    setAutoSuggestState(prev => ({
      ...prev,
      visible: true,
      loading: true,
      brand_description: '',
      ai_company_summary: '',
      errorMessage: '',
    }));
    try {
      const res = await fetch(`${baseUrl}/api/client/profile/about/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          company_name: profileData.company_name,
          company_website: profileData.company_website || '',
          industry: profileData.industry || '',
          products_services: profileData.products_services || '',
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        // Iter-257 — Surface FastAPI's structured detail (could be a
        // string OR an array of validation errors). Was throwing
        // "[object Object]" before, leading to the "Generation failed"
        // banner with no context.
        const msg = (typeof j.detail === 'string')
          ? j.detail
          : Array.isArray(j.detail)
              ? j.detail.map(d => d.msg).filter(Boolean).join(', ')
              : 'Generation failed';
        throw new Error(msg);
      }
      const j = await res.json();
      setAutoSuggestState({
        visible: true,
        loading: false,
        brand_description: j.brand_description || '',
        ai_company_summary: j.ai_company_summary || '',
        errorMessage: '',
      });
    } catch (e) {
      setAutoSuggestState({ visible: true, loading: false, brand_description: '', ai_company_summary: '', errorMessage: e.message || 'Could not generate suggestion' });
    }
  };

  const handleAutoSuggestUse = async () => {
    // Iter-245 — "Use This Description": populate the About fields and
    // save them server-side immediately. NOT auto-public — about_public
    // stays whatever the user already had (FALSE by default).
    const next = {
      ...aboutData,
      brand_description: autoSuggestState.brand_description || aboutData.brand_description,
      ai_company_summary: autoSuggestState.ai_company_summary || aboutData.ai_company_summary,
    };
    setAboutData(next);
    setSavingAbout(true);
    try {
      const res = await fetch(`${baseUrl}/api/client/profile/about`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          brand_description: next.brand_description,
          ai_company_summary: next.ai_company_summary,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('AI About description saved. You can edit it any time.');
      setAutoSuggestState(s => ({ ...s, visible: false }));
      // Remember we've shown it so we don't re-prompt.
      if (profile?.user_id) {
        localStorage.setItem(`about_autosuggest_dismissed:${profile.user_id}`, '1');
      }
    } catch (e) {
      toast.error('Could not save the AI description. Try Save About Profile manually.');
    } finally {
      setSavingAbout(false);
    }
  };

  const handleAutoSuggestEdit = () => {
    // Drop the AI text into the editor so the client can tweak before
    // saving. Does NOT persist.
    setAboutData(prev => ({
      ...prev,
      brand_description: autoSuggestState.brand_description || prev.brand_description,
      ai_company_summary: autoSuggestState.ai_company_summary || prev.ai_company_summary,
    }));
    setAutoSuggestState(s => ({ ...s, visible: false }));
    toast.message('Edit your About copy below, then click Save About Profile.');
    // Scroll the About section into view.
    setTimeout(() => {
      const el = document.getElementById('section-about');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleAutoSuggestDismiss = () => {
    setAutoSuggestState(s => ({ ...s, visible: false }));
    if (profile?.user_id) {
      localStorage.setItem(`about_autosuggest_dismissed:${profile.user_id}`, '1');
    }
  };

  const toggleEventType = (et) => {
    setAboutData(prev => {
      const has = prev.preferred_event_types.includes(et);
      return {
        ...prev,
        preferred_event_types: has
          ? prev.preferred_event_types.filter(x => x !== et)
          : [...prev.preferred_event_types, et],
      };
    });
  };

  const handleSaveAbout = async () => {
    setSavingAbout(true);
    try {
      const res = await fetch(`${baseUrl}/api/client/profile/about`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(aboutData),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || 'Save failed');
      }
      toast.success('About profile saved');
    } catch (e) {
      console.error('About save error:', e);
      toast.error('Failed to save About profile');
    } finally {
      setSavingAbout(false);
    }
  };

  const handleGenerateAbout = async () => {
    if (!formData.company_name && !aboutData.company_website && !aboutData.industry && !aboutData.products_services) {
      toast.error('Add at least a company name plus a website, industry, or products/services description first.');
      return;
    }
    setGeneratingAbout(true);
    try {
      const res = await fetch(`${baseUrl}/api/client/profile/about/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          company_name: formData.company_name,
          company_website: aboutData.company_website,
          industry: aboutData.industry,
          products_services: aboutData.products_services,
          brand_personality: aboutData.brand_personality,
          preferred_event_types: aboutData.preferred_event_types,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        // Iter-257 — Surface FastAPI's structured detail (could be a
        // string OR an array of validation errors). Was throwing
        // "[object Object]" before, leading to the "Generation failed"
        // banner with no context.
        const msg = (typeof j.detail === 'string')
          ? j.detail
          : Array.isArray(j.detail)
              ? j.detail.map(d => d.msg).filter(Boolean).join(', ')
              : 'Generation failed';
        throw new Error(msg);
      }
      const j = await res.json();
      setAboutData(prev => ({
        ...prev,
        brand_description: j.brand_description || prev.brand_description,
        ai_company_summary: j.ai_company_summary || prev.ai_company_summary,
      }));
      toast.success('AI draft ready — review and Save');
    } catch (e) {
      console.error('AI generate error:', e);
      toast.error(e.message || 'Could not generate About copy right now');
    } finally {
      setGeneratingAbout(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setUploading(true);
    
    try {
      // Get upload signature from backend
      const signatureRes = await fetch(`${baseUrl}/api/cloudinary/signature`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ folder: 'client_logos' }),
      });
      
      if (!signatureRes.ok) {
        throw new Error('Failed to get upload signature');
      }
      
      const { signature, timestamp, cloud_name, api_key, folder } = await signatureRes.json();
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', folder);
      
      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!uploadRes.ok) {
        throw new Error('Upload failed');
      }
      
      const uploadData = await uploadRes.json();
      
      // Update logo URL in backend
      const updateRes = await fetch(`${baseUrl}/api/client/profile/logo?logo_url=${encodeURIComponent(uploadData.secure_url)}`, {
        method: 'PATCH',
        headers,
      });
      
      if (!updateRes.ok) {
        throw new Error('Failed to save logo');
      }
      
      setProfile(prev => ({ ...prev, logo_url: uploadData.secure_url }));
      toast.success('Logo uploaded successfully');
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    
    try {
      const body = {};
      if (formData.company_name) body.company_name = formData.company_name;
      if (formData.contact_name) body.contact_name = formData.contact_name;
      if (formData.billing_address) body.billing_address = formData.billing_address;

      const res = await fetch(`${baseUrl}/api/client/profile`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      
      if (!res.ok) {
        throw new Error('Failed to save profile');
      }
      
      toast.success('Profile updated successfully');
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="client-profile-page">
      {/* Iter-245 — AI Auto-Suggest banner. Fires the first time a
          client logs in and the About section is blank. Three CTAs:
          Use This Description / Edit Description / Dismiss. Never
          auto-publishes (about_public stays whatever the client has). */}
      {autoSuggestState.visible && (
        <div className="max-w-2xl mx-auto px-4 mb-4">
          <div
            className="rounded-lg border-2 border-violet-300 bg-gradient-to-br from-violet-50 to-fuchsia-50 p-4 shadow-sm"
            data-testid="about-autosuggest-banner"
          >
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-violet-600" />
              <span className="text-sm font-semibold text-violet-800">
                AI-Generated About Suggestion
              </span>
            </div>
            {autoSuggestState.loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 py-3" data-testid="about-autosuggest-loading">
                <Loader2 className="w-4 h-4 animate-spin" />
                Building an About draft for you using your company name…
              </div>
            ) : autoSuggestState.errorMessage ? (
              <div className="text-sm text-red-600">
                {autoSuggestState.errorMessage}
                <Button
                  variant="link"
                  size="sm"
                  className="text-violet-700 px-2 ml-2"
                  onClick={handleAutoSuggestDismiss}
                  data-testid="about-autosuggest-dismiss-btn"
                >
                  Dismiss
                </Button>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-600 mb-2">
                  Talent and AI recaps work better with brand context. Here&apos;s a starting point — pick how to use it:
                </p>
                {autoSuggestState.brand_description && (
                  <div className="rounded bg-white border border-violet-200 p-2 mb-2 text-xs text-gray-700"
                       data-testid="about-autosuggest-brand-description">
                    <strong className="text-violet-700">Brand description:</strong>{' '}
                    {autoSuggestState.brand_description}
                  </div>
                )}
                {autoSuggestState.ai_company_summary && (
                  <div className="rounded bg-white border border-violet-200 p-2 mb-3 text-xs text-gray-700"
                       data-testid="about-autosuggest-summary">
                    <strong className="text-violet-700">Company summary:</strong>{' '}
                    {autoSuggestState.ai_company_summary}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleAutoSuggestUse}
                    disabled={savingAbout}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    size="sm"
                    data-testid="about-autosuggest-use-btn"
                  >
                    {savingAbout ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1.5" />}
                    Use This Description
                  </Button>
                  <Button
                    onClick={handleAutoSuggestEdit}
                    variant="outline"
                    size="sm"
                    className="border-violet-300 text-violet-700 hover:bg-violet-100"
                    data-testid="about-autosuggest-edit-btn"
                  >
                    Edit Description
                  </Button>
                  <Button
                    onClick={handleAutoSuggestDismiss}
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:bg-gray-100"
                    data-testid="about-autosuggest-dismiss-btn"
                  >
                    Dismiss
                  </Button>
                </div>
                <p className="text-[10px] text-gray-500 mt-2">
                  Nothing is published until you flip &quot;Make About profile public&quot; below.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/client/dashboard')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Company Profile</CardTitle>
            <CardDescription className="text-gray-600">
              Manage your company information and logo
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Logo Upload — anchor: #logo (Branding/Logo Quick Action) */}
            <div id="section-logo" className="rounded-lg p-2 -m-2 transition-shadow" data-testid="profile-section-logo">
              <Label className="text-gray-700 mb-2 block">Company Logo</Label>
              <div className="flex items-center gap-6">
                <div className="relative">
                  {profile?.logo_url ? (
                    <img 
                      src={profile.logo_url}
                      alt="Company Logo"
                      className="w-24 h-24 rounded-lg object-contain border-2 border-gray-200 bg-white"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                      <Building2 className="w-10 h-10 text-gray-400" />
                    </div>
                  )}
                  {profile?.logo_url && (
                    <CheckCircle className="w-5 h-5 text-green-500 absolute -top-2 -right-2 bg-white rounded-full" />
                  )}
                </div>
                
                <div className="flex-1">
                  <Button
                    variant="outline"
                    className="border-gray-300"
                    onClick={() => document.getElementById('logo-input')?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {profile?.logo_url ? 'Change Logo' : 'Upload Logo'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    PNG, JPG up to 5MB. Recommended: 400x400px
                  </p>
                  <input
                    id="logo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="logo-input"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                Your logo will appear on gig listings and booking views.
              </p>
            </div>
            
            {/* Company Name — anchor: #company (Company Info Quick Action) */}
            <div id="section-company" className="space-y-2 rounded-lg p-2 -m-2 transition-shadow" data-testid="profile-section-company">
              <Label htmlFor="company_name" className="text-gray-700">Company Name</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Your Company Name"
                className="bg-white border-gray-300"
                data-testid="company-name-input"
              />
            </div>
            
            {/* Contact Name — anchor: #contact (Edit Profile Quick Action) */}
            <div id="section-contact" className="space-y-2 rounded-lg p-2 -m-2 transition-shadow" data-testid="profile-section-contact">
              <Label htmlFor="contact_name" className="text-gray-700">Primary Contact</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="Your Name"
                className="bg-white border-gray-300"
                data-testid="contact-name-input"
              />
            </div>
            
            {/* Billing Address */}
            <div className="space-y-2">
              <Label className="text-gray-700 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Billing Address
              </Label>
              <AddressAutocomplete
                value={formData.billing_address}
                onChange={(val) => setFormData({ ...formData, billing_address: val })}
                onSelect={(data) => {
                  if (data) {
                    setFormData({ ...formData, billing_address: data.address });
                  }
                }}
                placeholder="Start typing your address..."
                data-testid="billing-address-input"
              />
              <p className="text-xs text-gray-400">Used for invoicing and billing purposes</p>
            </div>
            
            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="text-gray-700">Email</Label>
              <Input
                value={profile?.email || ''}
                disabled
                className="bg-gray-100 border-gray-200 text-gray-500"
              />
              <p className="text-xs text-gray-400">Email cannot be changed</p>
            </div>
            
            {/* About Client — Iter-245
                Full Brand Profile + AI Assist. Replaces the prior
                "coming soon" placeholder. */}
            <div id="section-about" className="space-y-4 rounded-lg p-4 -m-1 bg-violet-50 border border-violet-200" data-testid="profile-section-about">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Label className="text-gray-800 flex items-center gap-2 mb-1">
                    <Info className="w-4 h-4 text-violet-600" />
                    About Client
                  </Label>
                  <p className="text-xs text-gray-600">
                    Tell talent and the platform who you are. Use AI Assist to draft a starting point, then edit anything.
                  </p>
                </div>
                <Button
                  onClick={handleGenerateAbout}
                  disabled={generatingAbout}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  size="sm"
                  data-testid="about-ai-generate-btn"
                >
                  {generatingAbout ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" />AI Assist</>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-700 text-sm">Industry</Label>
                  <Select
                    value={aboutData.industry || ''}
                    onValueChange={(v) => setAboutData({ ...aboutData, industry: v })}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="about-industry-select">
                      <SelectValue placeholder="Select an industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRY_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-700 text-sm flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5" /> Website
                  </Label>
                  <Input
                    value={aboutData.company_website}
                    onChange={(e) => setAboutData({ ...aboutData, company_website: e.target.value })}
                    placeholder="https://your-company.com"
                    className="bg-white border-gray-300"
                    data-testid="about-website-input"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-700 text-sm">Brand Description</Label>
                <Textarea
                  value={aboutData.brand_description}
                  onChange={(e) => setAboutData({ ...aboutData, brand_description: e.target.value })}
                  placeholder="2–3 sentences about your brand."
                  className="bg-white border-gray-300 min-h-[70px]"
                  data-testid="about-brand-description-input"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-gray-700 text-sm">Products / Services</Label>
                <Textarea
                  value={aboutData.products_services}
                  onChange={(e) => setAboutData({ ...aboutData, products_services: e.target.value })}
                  placeholder="What you sell or offer (1–2 sentences)."
                  className="bg-white border-gray-300 min-h-[60px]"
                  data-testid="about-products-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-700 text-sm">Brand Personality</Label>
                  <Select
                    value={aboutData.brand_personality || ''}
                    onValueChange={(v) => setAboutData({ ...aboutData, brand_personality: v })}
                  >
                    <SelectTrigger className="bg-white border-gray-300" data-testid="about-personality-select">
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRAND_PERSONALITY_OPTIONS.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-700 text-sm">Preferred Event Types</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="about-event-types-group">
                    {EVENT_TYPE_OPTIONS.map(et => {
                      const active = aboutData.preferred_event_types.includes(et);
                      return (
                        <button
                          type="button"
                          key={et}
                          onClick={() => toggleEventType(et)}
                          className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${active ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-gray-700 border-gray-300 hover:border-violet-400'}`}
                          data-testid={`about-event-type-${et.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {et}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-gray-700 text-sm">Social Links</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/your-brand' },
                    { key: 'linkedin',  label: 'LinkedIn',  icon: Linkedin,  placeholder: 'https://linkedin.com/company/your-brand' },
                    { key: 'facebook',  label: 'Facebook',  icon: Facebook,  placeholder: 'https://facebook.com/your-brand' },
                    { key: 'twitter',   label: 'X / Twitter', icon: Twitter, placeholder: 'https://x.com/your-brand' },
                    { key: 'tiktok',    label: 'TikTok',    icon: Globe,     placeholder: 'https://tiktok.com/@your-brand' },
                  ].map(s => (
                    <div key={s.key} className="flex items-center gap-2">
                      <s.icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <Input
                        value={aboutData.social_links?.[s.key] || ''}
                        onChange={(e) => setAboutData({
                          ...aboutData,
                          social_links: { ...aboutData.social_links, [s.key]: e.target.value },
                        })}
                        placeholder={s.placeholder}
                        className="bg-white border-gray-300 text-xs"
                        data-testid={`about-social-${s.key}-input`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-gray-700 text-sm">AI Company Summary</Label>
                  {aboutData.ai_company_summary && (
                    <Badge variant="outline" className="text-[10px] border-violet-300 text-violet-700">
                      AI draft
                    </Badge>
                  )}
                </div>
                <Textarea
                  value={aboutData.ai_company_summary}
                  onChange={(e) => setAboutData({ ...aboutData, ai_company_summary: e.target.value })}
                  placeholder="One paragraph summary. Use AI Assist to draft this, then edit."
                  className="bg-white border-gray-300 min-h-[90px]"
                  data-testid="about-summary-input"
                />
              </div>

              <div className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 border border-gray-200">
                <div>
                  <p className="text-sm font-medium text-gray-800">Make About profile public</p>
                  <p className="text-[11px] text-gray-500">When ON, talent can see this profile on gig listings.</p>
                </div>
                <Switch
                  checked={!!aboutData.about_public}
                  onCheckedChange={(v) => setAboutData({ ...aboutData, about_public: v })}
                  data-testid="about-public-switch"
                />
              </div>

              <Button
                onClick={handleSaveAbout}
                disabled={savingAbout}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-about-btn"
              >
                {savingAbout ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                ) : (
                  'Save About Profile'
                )}
              </Button>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientProfile;
